import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { map, switchMap, catchError, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  UserActionTypes,
  LoadUsersSuccess,
  LoadUsersFailed,
  BulkEditAccountUserPermissionsSuccess,
  BulkEditAccountUserPermissionsFailure,
  DownloadPermissionRemoveCSVSuccess,
  DownloadPermissionRemoveCSVFailure,
  DownloadAccountUserCSVSuccess,
  DownloadAccountUserCSVFailure,
  LoadUsers,
  LoadAccountUsers,
} from './users.actions';
import * as fromUsers from '@account-users/user/state';
import { UserService } from '@account-users/user/services/user.service';
import { Store, select } from '@ngrx/store';
import { UserState } from './users.reducer';
import { ToastrService } from 'ngx-toastr';

@Injectable()
export class UsersEffects {
  @Effect()
  fethAllUsers$ = this.actions$.pipe(
    ofType(UserActionTypes.LOAD_USERS),
    switchMap(({ payload: { query, roles } }: LoadUsers) =>
      this.userService.searchUsers(query, roles).pipe(
        map((response) => new LoadUsersSuccess(response['users'])),
        catchError((error) => {
          this.toastr.error('Could not fetch users', 'Request failed');
          return of(new LoadUsersFailed());
        }),
      ),
    ),
  );

  @Effect()
  fethAllAccountUsers$ = this.actions$.pipe(
    ofType(UserActionTypes.LOAD_ACCOUNT_USERS),
    switchMap(
      ({
        payload: { query, roles, accountCategories, permissions },
      }: LoadAccountUsers) => {
        if (
          (query && query.length) ||
          (roles && roles.length) ||
          (accountCategories && accountCategories.length) ||
          (permissions && permissions.length)
        ) {
          return this.userService
            .searchAccountUsers(query, roles, permissions, accountCategories)
            .pipe(
              map((response) => new LoadUsersSuccess(response)),
              catchError((error) => of(new LoadUsersFailed())),
            );
        } else {
          return [new LoadUsersFailed()];
        }
      },
    ),
  );

  @Effect()
  bulkEditAccountUsersPermissions$ = this.actions$.pipe(
    ofType(UserActionTypes.BULK_EDIT_ACCOUNT_USER_PERMISSIONS),
    withLatestFrom(this.store.pipe(select(fromUsers.getBulkEdit))),
    switchMap(([_, bulkEdit]) =>
      this.userService.bulkEditAccountUsersPermissions(bulkEdit).pipe(
        map((response) => new BulkEditAccountUserPermissionsSuccess(response)),
        catchError((error) => {
          this.toastr.error(
            'Could not remove permission of users',
            'Request failed',
          );
          return of(new BulkEditAccountUserPermissionsFailure());
        }),
      ),
    ),
  );

  @Effect()
  downloadCSV = this.actions$.pipe(
    ofType(UserActionTypes.DOWNLOAD_BULK_PERMISSION_CSV),
    withLatestFrom(
      this.store.pipe(select(fromUsers.getBulkEditAccountUserCSV)),
    ),
    map(([_, { csv }]) => {
      this.download(csv);
      return new DownloadPermissionRemoveCSVSuccess();
    }),
    catchError((error) => {
      this.toastr.error('Could not download CSV', 'Request failed');
      return of(new DownloadPermissionRemoveCSVFailure());
    }),
  );

  @Effect()
  downloadAccountUserCSV = this.actions$.pipe(
    ofType(UserActionTypes.DOWNLOAD_ACCOUNT_USER_CSV),
    withLatestFrom(this.store.pipe(select(fromUsers.getAccountUserCSV))),
    map(([_, { csv }]) => {
      this.download(csv);
      return new DownloadAccountUserCSVSuccess();
    }),
    catchError((error) => {
      this.toastr.error('Could not download CSV', 'Request failed');
      return of(new DownloadAccountUserCSVFailure());
    }),
  );

  download(csv: string) {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const fileName = `${Date.now()}.csv`;
    const anchorEl = document.createElement('a');
    document.body.appendChild(anchorEl);

    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(blob, fileName);
    } else {
      const url = window.URL.createObjectURL(blob);
      anchorEl.href = url;
      anchorEl.download = fileName;
      anchorEl.click();
      window.URL.revokeObjectURL(url);
    }

    document.body.removeChild(anchorEl);
  }

  constructor(
    private actions$: Actions,
    private userService: UserService,
    private toastr: ToastrService,
    private readonly store: Store<UserState>,
  ) {}
}
